import { Fragment, useCallback, useContext, useState } from "react";
import { groupBy } from "lodash";
import { SEARCH_RESULT_TYPE } from "common/constants";
import {
  displayForResult,
  SEARCH_RESULT_TYPE_LABELS,
} from "common-client/utils/search";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { EmptyState } from "../Common/EmptyState";
import { IllustrationName } from "../Common/Illustration";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { NoSearchResultsContent } from "./__styles__/OmniboxSearch";
import { SEARCH_ERROR } from "./hooks/useSearchResultsState";
import { SearchContext } from "./SearchContext";
import { SearchEmptyState, SearchLoadingState } from "./SearchEmptyState";

const SEARCH_ERROR_MESSAGES: Record<
  SEARCH_ERROR,
  { title: string; body: string; icon: IllustrationName }
> = {
  [SEARCH_ERROR.COORDINATES_OUTSIDE_BOUNDARY]: {
    title: "No results found",
    body: "These coordinates are outside your account. Try searching within your area.",
    icon: "missing-location",
  },
  [SEARCH_ERROR.UNKNOWN]: {
    title: "Search unavailable",
    body: "An error occurred while searching. Please try again.",
    icon: "missing-location",
  },
};

const OmniboxResultItem = ({
  result,
  hoveredOnMap,
}: {
  result: OmniboxSearchResult;
  hoveredOnMap?: boolean;
}) => {
  const [hoveredInResults, setHoveredInResults] = useState(hoveredOnMap);
  const { searchResultsDispatch } = useContext(SearchContext);
  const { title, description, iconName } = displayForResult({ result });

  const onResultHover = useCallback(
    (result: OmniboxSearchResult) => {
      setHoveredInResults(true);
      searchResultsDispatch({
        type: "setHoveredSearchResult",
        feature: {
          id: result.id,
          featureType:
            result.__typename === "ParcelSearchResult" ?
              SEARCH_RESULT_TYPE.PARCEL
            : SEARCH_RESULT_TYPE.PROPERTY,
        },
        hoverSource: "results",
      });
    },
    [searchResultsDispatch],
  );

  const onResultLeave = useCallback(() => {
    setHoveredInResults(false);
    searchResultsDispatch({
      type: "clearHoveredSearchResult",
    });
  }, [searchResultsDispatch]);

  const isHovered = hoveredInResults || hoveredOnMap;

  return (
    <SelectionCard.Item
      key={result.id}
      size="compact"
      borderStyle="ghost"
      value={result}
      onMouseEnter={() => {
        onResultHover(result);
      }}
      onMouseLeave={() => {
        onResultLeave();
      }}
      hover={isHovered}
    >
      <SelectionCard.CommonContent
        iconName={iconName}
        title={title}
        description={description}
      />
    </SelectionCard.Item>
  );
};

export const OmniboxResults = ({
  expandPlaceholders,
}: {
  expandPlaceholders: boolean;
}) => {
  const { searchResultsState, handleSearchResultClick } =
    useContext(SearchContext);

  const isHoveredOnMap = useCallback(
    (result: OmniboxSearchResult) => {
      const hoveredSearchResult = searchResultsState.hoveredSearchResult;
      if (!hoveredSearchResult) {
        return false;
      }
      return (
        hoveredSearchResult.feature.id === result.id &&
        hoveredSearchResult.feature.featureType === result.__typename &&
        hoveredSearchResult.hoverSource === "map"
      );
    },
    [searchResultsState.hoveredSearchResult],
  );

  if (
    !searchResultsState.debouncedQuery ||
    searchResultsState.debouncedQuery.length < 3
  ) {
    return <NoSearchResultsContent />;
  } else if (searchResultsState.loading) {
    return <SearchLoadingState expand={expandPlaceholders} />;
  } else if (searchResultsState.error) {
    const errorMessage = SEARCH_ERROR_MESSAGES[searchResultsState.error];
    return (
      <SearchEmptyState
        expandPlaceholders={expandPlaceholders}
        title={errorMessage.title}
        icon={errorMessage.icon}
        body={errorMessage.body}
      />
    );
  } else if (!searchResultsState.results?.length) {
    return (
      <SearchEmptyState
        expandPlaceholders={expandPlaceholders}
        title="No results found"
        icon="missing-location"
        body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
      />
    );
  }

  const results = searchResultsState.results;
  const hasStrictMatch = results.some(result => result.strictMatch);
  const resultsByType = groupBy(results, r => r.__typename);
  const resultTypes = Object.values(SEARCH_RESULT_TYPE);

  return (
    <SelectionCard.Group
      data-testid="omnibox-results"
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {!hasStrictMatch && (
        <EmptyState compact title="No exact matches, showing related results" />
      )}
      {resultTypes
        .filter(type => resultsByType[type]?.length)
        .map(type => (
          <Fragment key={type}>
            <Body
              type="emphasis"
              size="small"
              style={{
                padding: `${spacing.s} ${spacing.m} 0`,
              }}
            >
              {SEARCH_RESULT_TYPE_LABELS[type]}
            </Body>
            {resultsByType[type]!.map(result => (
              <OmniboxResultItem
                result={result}
                key={result.id}
                hoveredOnMap={isHoveredOnMap(result)}
              />
            ))}
          </Fragment>
        ))}
    </SelectionCard.Group>
  );
};
